import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";

// =============================================================================
// useDMAnalytics
// =============================================================================
// PURPOSE: DM engagement analytics for Instagram conversations with setter attribution.
//
// ARCHITECTURE:
// - Calls RPC `get_dm_analytics_fast` (reads from pre-aggregated daily_dm_metrics table)
// - 15-min localStorage cache with TTL and org scoping
// - Background refresh after showing cached data (instant UX + fresh data)
// - AbortController cleanup for race conditions on org switching
//
// SETTER ATTRIBUTION:
// - Patil, Alice, Alyssa, ManyChat detection (see docs/DM_ANALYTICS_SPEC.md)
// - Ghost conversations (system-only messages) excluded from stats
//
// METRICS TRACKED:
// - New Chats: Lead-initiated first messages only
// - Outbound Attempts: All setter-initiated first messages (includes ManyChat)
// - Engagement Rate: (Engaged / Total Activity) * 100
// - Conversion Rate: (Real Convos / Engaged) * 100
// - Response/Ghost rates for manual and ManyChat outreach
//
// KEY DECISIONS:
// - Cache shows instant results, then refreshes in background (perceived perf)
// - Daily breakdown for trend charts (Recharts format)
// - Recent conversations for sample modals (DMSampleModal)
// =============================================================================

import { CACHE_VERSION } from "../lib/cacheVersion";
import { formatDateKeyDisplay, toOrgDateKey, toOrgStartISO, toOrgEndISO } from "../lib/timezone";

// --- Cache Configuration ---
const CACHE_PREFIX = `altara_${CACHE_VERSION}_dm_analytics_`;
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes - live ranges (includes today)
const HISTORICAL_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours - historical ranges never change

interface CacheEntry {
  data: DMAnalyticsData;
  timestamp: number;
  orgId: string;
  dateKey: string;
}

/** Exported for prefetch system */
export const getDMAnalyticsCacheKey = (
  orgId: string,
  start: Date | null,
  end: Date | null
): string => {
  const startStr = start ? toOrgDateKey(start) : "all";
  const endStr = end ? toOrgDateKey(end) : "all";
  return `${CACHE_PREFIX}${orgId}_${startStr}_${endStr}`;
};

const isHistoricalRange = (end: Date | null): boolean => {
  if (!end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end < today;
};

const getCache = (orgId: string, start: Date | null, end: Date | null): DMAnalyticsData | null => {
  try {
    const key = getDMAnalyticsCacheKey(orgId, start, end);
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const entry: CacheEntry = JSON.parse(raw);
    const ttl = isHistoricalRange(end) ? HISTORICAL_CACHE_TTL : CACHE_TTL;
    const isExpired = Date.now() - entry.timestamp > ttl;

    if (isExpired) {
      localStorage.removeItem(key);
      return null;
    }

    // Guard: prefetch stores raw RPC data (snake_case keys) into the same cache.
    // Detect raw data shape and reject it — the hook will re-fetch and parse correctly.
    if (
      entry.data &&
      !("totalNewDMs" in entry.data) &&
      "total_new_dms" in (entry.data as Record<string, unknown>)
    ) {
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
};

/** SWR helper: returns expired cached data without deleting it. Used to show stale data
 * while a background re-fetch is in progress. Returns null only if nothing was ever cached. */
const getStaleCache = (
  orgId: string,
  start: Date | null,
  end: Date | null
): DMAnalyticsData | null => {
  try {
    const key = getDMAnalyticsCacheKey(orgId, start, end);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    // Reject raw RPC data (snake_case keys from prefetch)
    if (
      entry.data &&
      !("totalNewDMs" in entry.data) &&
      "total_new_dms" in (entry.data as Record<string, unknown>)
    ) {
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
};

/** Write DM analytics data to cache. Exported for use by prefetch system. */
export const setDMAnalyticsCache = (
  orgId: string,
  start: Date | null,
  end: Date | null,
  data: DMAnalyticsData
): void => {
  try {
    const key = getDMAnalyticsCacheKey(orgId, start, end);
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      orgId,
      dateKey: `${start?.toISOString() || "all"}_${end?.toISOString() || "all"}`,
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    logger.warn("DM Analytics cache write failed");
  }
};

// Clean up old cache entries (called on mount)
const cleanupOldCache = (): void => {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const entry: CacheEntry = JSON.parse(raw);
            // Use the longer TTL for cleanup so historical caches (24h) aren't evicted at 30min
            if (now - entry.timestamp > HISTORICAL_CACHE_TTL * 2) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          localStorage.removeItem(key!);
        }
      }
    }
  } catch {
    // Ignore cleanup errors
  }
};

interface DailyBreakdown {
  date: string;
  new_dms: number; // RAW conversations created this day, ANY source (lead/setter/ManyChat) — NOT the lead-replied metric. Charted as "Conversations Created".
  new_dms_lead_replied: number; // REAL new chats: created this day where the lead actually messaged + assigned to a setter. Per-day version of totalNewDMsLeadReplied. Charted as "New Chats".
  outbound_attempts: number; // All setter-initiated
  manychat: number; // ManyChat automation
  engaged: number; // setter-touched (rendered as "Replied")
  real_convos: number; // 4+ setter reply turns, started (rendered as "Conversations Started")
  ghosted: number;
}

interface RecentConversation {
  conversation_id: string; // Added for click-to-view functionality
  contact_name: string;
  lead_instagram: string | null;
  first_sender: "lead" | "setter";
  first_message: string;
  lead_msgs: number;
  setter_msgs: number;
  total_msgs: number;
  first_msg_at: string;
  is_manychat: boolean;
  is_engaged: boolean;
  is_real_convo: boolean;
}

// V2 Funnel metrics (lead-first only, based on first_lead_msg_date, MUST add up)
interface V2Funnel {
  newDms: number; // Lead's first message on this date
  realConvos: number; // "Deep Convos" — 4+ setter reply turns (canonical per /metrics SSOT)
  engagedActive: number; // 2-4 msgs each, last reply <24h
  needsReply: number; // 2+ msgs each, lead sent last >24h ago (setter owes reply)
  needsFollowUp: number; // 2+ msgs each, setter sent last >24h ago (lead went silent)
  ghosted: number; // 2+ consecutive follow-up days, no reply
  unclassified: number; // Too early to categorize
}

export interface DMAnalyticsData {
  // Summary (corrected definitions)
  totalNewDMs: number; // RAW count of conversations created in window (any source — kept for engagement rate denominator + the never-replied subtext)
  totalNewDMsLeadReplied: number; // NEW HERO NUMBER (Apr 30): conversations created in window where lead has >=1 message — excludes ManyChat blasts the lead never engaged with
  totalOutboundAttempts: number; // All setter-initiated first messages
  totalManyChat: number; // ManyChat automation subset
  totalManualOutbound: number; // Manual setter outreach
  totalFollowUps: number; // Setter→Setter on different days
  totalEngaged: number; // Both sides messaged
  totalRealConvos: number; // Conversations Started — 4+ setter reply turns, first_msg_date attribution. Pairs with totalDeepConvos (Conversations Worked, work_date attribution) for Sales Cycle Drift.
  totalDeepConvos: number; // 4+ setter reply turns (canonical per /metrics SSOT)
  totalGhosted: number; // Setter initiated, no reply
  // Derived rates
  conversationRate: number; // Real Convos / Engaged
  ghostRate: number; // Ghosted / Outbound Attempts
  responseRate: number; // (Outbound - Ghosted) / Outbound
  // V2 Funnel (Feb 2026) - metrics that add up
  v2Funnel: V2Funnel;
  // Daily
  dailyBreakdown: DailyBreakdown[];
  // Recent
  recentConversations: RecentConversation[];
}

const DEFAULT_V2_FUNNEL: V2Funnel = {
  newDms: 0,
  realConvos: 0,
  engagedActive: 0,
  needsReply: 0,
  needsFollowUp: 0,
  ghosted: 0,
  unclassified: 0,
};

const DEFAULT_DATA: DMAnalyticsData = {
  totalNewDMs: 0,
  totalNewDMsLeadReplied: 0,
  totalOutboundAttempts: 0,
  totalManyChat: 0,
  totalManualOutbound: 0,
  totalFollowUps: 0,
  totalEngaged: 0,
  totalRealConvos: 0,
  totalDeepConvos: 0,
  totalGhosted: 0,
  conversationRate: 0,
  ghostRate: 0,
  responseRate: 0,
  v2Funnel: DEFAULT_V2_FUNNEL,
  dailyBreakdown: [],
  recentConversations: [],
};

export const useDMAnalytics = (
  organizationId: string | null,
  dateRange: { start: Date | null; end: Date | null },
  timezone?: string
) => {
  const [data, setData] = useState<DMAnalyticsData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasCleanedUp = useRef(false);

  // Cleanup old cache entries on first mount
  useEffect(() => {
    if (!hasCleanedUp.current) {
      cleanupOldCache();
      hasCleanedUp.current = true;
    }
  }, []);

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!supabase || !organizationId) {
        setData(DEFAULT_DATA);
        return;
      }

      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const controller = abortControllerRef.current; // local capture — guards NEVER read abortControllerRef.current directly

      const startDate = dateRange.start ? new Date(dateRange.start) : null;
      const endDate = dateRange.end ? new Date(dateRange.end) : null;

      // Stale-while-revalidate: check fresh cache first, then stale cache
      if (!forceRefresh) {
        const cached = getCache(organizationId, startDate, endDate);
        if (cached) {
          logger.debug("DM Analytics: Using cached data (fresh)");
          setData(cached);
          // Fresh cache — still re-fetch in background but no spinner
        } else {
          // Cache expired or missing — try showing stale data (SWR)
          const stale = getStaleCache(organizationId, startDate, endDate);
          if (stale) {
            logger.debug("DM Analytics: Using stale data (SWR), re-fetching in background");
            setData(stale);
            // Stale data showing — no spinner, background re-fetch below
          } else {
            // Nothing cached at all — show spinner
            setLoading(true);
          }
        }
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        // Org-tz-correct UTC bounds for get_dm_analytics_fast — its body does
        // `(p_start_date AT TIME ZONE v_timezone)::DATE`, so it needs the UTC
        // instant of org-midnight/org-end-of-day, not a raw UTC-midnight window.
        // Was: startDate?.toISOString() / endDate?.toISOString() after mutating
        // endDate with a plain endDate.setHours(23,59,59,999) — that's UTC
        // end-of-day, not org end-of-day, so for negative-UTC-offset orgs
        // (e.g. America/Chicago) this window diverged from usePortalKpis.ts's
        // get_dm_analytics_fast window by up to 1 day on both bounds. Same
        // helpers usePortalKpis.ts already uses for get_operations_kpis.
        const startISO = startDate ? toOrgStartISO(startDate, timezone) : null;
        const endISO = endDate ? toOrgEndISO(endDate, timezone) : null;

        // Use the fast pre-aggregated RPC (reads from daily_dm_metrics table).
        // NOT routed through lib/rpcCoalescer.ts's coalesceRpc() — this call
        // needs .abortSignal(controller.signal) below for the org-switch
        // race-condition guard documented at the top of this file, and
        // coalesceRpc has no abort hook. See usePortalKpis.ts's matching
        // get_dm_analytics_fast call site for the full reasoning — the two
        // calls now request an identical window (this fix) but are
        // deliberately left as two separate requests rather than sharing a
        // promise that an unrelated abort could reject.
        const { data: result, error: rpcError } = await supabase
          .rpc("get_dm_analytics_fast", {
            p_organization_id: organizationId,
            p_start_date: startISO,
            p_end_date: endISO,
          })
          .abortSignal(controller.signal);

        if (rpcError) {
          if (controller.signal.aborted) return; // abort surfaces as rpcError here
          logger.error("DM Analytics RPC error:", rpcError);
          setError(rpcError.message);
          return;
        }

        if (result && result.length > 0) {
          const r = result[0];
          const totalNewDMs = r.total_new_dms || 0;
          const totalNewDMsLeadReplied = r.total_new_dms_lead_replied || 0;
          const totalOutboundAttempts = r.total_outbound_attempts || 0;
          const totalManyChat = r.total_manychat || 0;
          const totalManualOutbound = r.total_manual_outbound || 0;
          const totalFollowUps = r.total_follow_ups || 0;
          const totalEngaged = r.total_engaged || 0;
          const totalRealConvos = r.total_real_convos || 0;
          const totalDeepConvos = r.total_deep_convos || 0;
          const totalGhosted = r.total_ghosted || 0;

          // Parse V2 funnel metrics
          const v2Raw = r.v2_funnel || {};
          const v2NewDms = v2Raw.new_dms || 0;
          const v2RealConvos = v2Raw.real_convos || 0;
          const v2EngagedActive = v2Raw.engaged_active || 0;
          const v2NeedsReply = v2Raw.needs_reply || 0;
          const v2NeedsFollowUp = v2Raw.needs_follow_up || 0;
          const v2Ghosted = v2Raw.ghosted || 0;
          const v2Unclassified = v2Raw.unclassified || 0;

          const newData: DMAnalyticsData = {
            totalNewDMs,
            totalNewDMsLeadReplied,
            totalOutboundAttempts,
            totalManyChat,
            totalManualOutbound,
            totalFollowUps,
            totalEngaged,
            totalRealConvos,
            totalDeepConvos,
            totalGhosted,
            // engagementRate DELETED 2026-08-18 (R61) — redundant with conversationRate
            // (setters touch ~99% of new chats, so deep/newChats ≈ deep/engaged).
            // Conversion rate = deep convos / engaged (4+ reply turns per SSOT)
            conversationRate: totalEngaged > 0 ? (totalDeepConvos / totalEngaged) * 100 : 0,
            // Ghost rate = ghosted / manual outbound (ManyChat excluded — negligence signal per WK17 SSOT)
            ghostRate: totalManualOutbound > 0 ? (totalGhosted / totalManualOutbound) * 100 : 0,
            // PARKED per metricMeta.ts ("responseRate" entry, ~line 690).
            // Live formula is (outbound - ghosted) / outbound; SSOT canonical formula
            // is "lead replied / outbound". Denominator scope diverges (manual+ManyChat
            // mix here vs. manual-only in SSOT). Not currently rendered anywhere.
            // If unparking: reconcile with SSOT first.
            responseRate:
              totalOutboundAttempts > 0
                ? ((totalOutboundAttempts - totalGhosted) / totalOutboundAttempts) * 100
                : 0,
            // V2 Funnel (Feb 2026)
            v2Funnel: {
              newDms: v2NewDms,
              realConvos: v2RealConvos,
              engagedActive: v2EngagedActive,
              needsReply: v2NeedsReply,
              needsFollowUp: v2NeedsFollowUp,
              ghosted: v2Ghosted,
              unclassified: v2Unclassified,
            },
            dailyBreakdown: r.daily_breakdown || [],
            recentConversations: r.recent_conversations || [],
          };

          setData(newData);
          // Cache the fresh data
          setDMAnalyticsCache(organizationId, startDate, endDate, newData);
          logger.debug("DM Analytics: Fetched and cached fresh data");
        }
      } catch (err) {
        if (controller.signal.aborted) return; // abort rejection — Supabase wraps these, signal is reliable
        // Ignore aborted requests
        if ((err as Error).name === "AbortError") return;
        logger.error("DM Analytics fetch error:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [organizationId, dateRange.start, dateRange.end, timezone]
  );

  useEffect(() => {
    fetchData();

    // Cleanup: abort in-flight request on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  // Chart data for custom SVG chart
  const chartData = useMemo(() => {
    const tz = timezone || "UTC";
    return (data.dailyBreakdown || []).map((d) => ({
      date: formatDateKeyDisplay(d.date, tz),
      fullDate: d.date,
      // "New Chats" = the REAL count (lead actually replied), NOT the raw all-source created total.
      // "Conversations Created" = the raw all-source total (ManyChat-inflated) — shown alongside so
      // the graph is explicit that most "created" convos are bot greets, not new leads.
      "New Chats": d.new_dms_lead_replied ?? 0,
      "Conversations Created": d.new_dms,
      Replied: d.engaged, // setter-touched (was mislabeled "Engaged")
      "Conversations Started": d.real_convos, // 4+ reply turns (was bare "Conversations")
      Unanswered: d.ghosted,
    }));
  }, [data.dailyBreakdown, timezone]);

  return {
    data,
    loading,
    error,
    chartData,
  };
};
